import React, { useState, useEffect, useMemo } from 'react';
import { Incident } from '../../types/iso27001';
import { storageService } from '../../services/storageService';
import { authService } from '../../services/authService';
import { aiService } from '../../services/aiService';
import { Modal } from '../ui/Modal';
import { Pagination } from '../ui/Pagination';
import { FilterBar } from '../ui/FilterBar';
import { Plus, Edit, Trash2, AlertOctagon, Loader2, Sparkles, Download } from 'lucide-react';
import { exportToCSV } from '../../utils/exportUtils';
import { IncidentAdvisorCard } from './IncidentAdvisorCard';

import { getUniqueOptions, filterItems } from '../../utils/filterUtils';

interface IncidentManagerProps {
  userRole: 'admin' | 'editor' | 'viewer';
  initialFilters?: {
    status?: string;
    severity?: string;
    reportedBy?: string;
  };
}

export const IncidentManager: React.FC<IncidentManagerProps> = ({ userRole, initialFilters }) => {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIncident, setEditingIncident] = useState<Incident | null>(null);
  const [formData, setFormData] = useState<Partial<Incident>>({});

  // Check permissions (section-based RBAC)
  const canEdit = authService.hasPermission('incidents', 'edit') || authService.isGlobalAdmin() || authService.isOrgAdmin();
  const canDelete = canEdit; // Edit permission includes delete

  const incidentTypes = [
    "Phishing", "Malware", "Ransomware", "DDoS", "Insider Threat", 
    "Data Leak", "Hardware Failure", "Software Bug", "Unauthorized Access", "Social Engineering"
  ];

  // Pagination & Filter
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState(initialFilters?.status || '');
  const [severityFilter, setSeverityFilter] = useState(initialFilters?.severity || '');
  const [reportedByFilter, setReportedByFilter] = useState(initialFilters?.reportedBy || '');

  // Update filters if initialFilters change
  useEffect(() => {
    if (initialFilters?.status !== undefined) setStatusFilter(initialFilters.status);
    if (initialFilters?.severity !== undefined) setSeverityFilter(initialFilters.severity);
    if (initialFilters?.reportedBy !== undefined) setReportedByFilter(initialFilters.reportedBy);
  }, [initialFilters]);

  useEffect(() => {
    setIncidents(storageService.getIncidents());
  }, []);

  const filteredIncidents = useMemo(() => {
    return filterItems(incidents, searchQuery, ['title', 'description', 'reportedBy'], {
      status: statusFilter,
      severity: severityFilter,
      reportedBy: reportedByFilter
    });
  }, [incidents, searchQuery, statusFilter, severityFilter, reportedByFilter]);

  const paginatedIncidents = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredIncidents.slice(start, start + pageSize);
  }, [filteredIncidents, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredIncidents.length / pageSize);

  // Dynamic Options
  const reportedByOptions = useMemo(() => getUniqueOptions(incidents, 'reportedBy'), [incidents]);

  const handleSave = () => {
    if (editingIncident) {
      storageService.updateIncident({ ...editingIncident, ...formData } as Incident);
    } else {
      const newIncident: Incident = {
        id: `INC-${Date.now()}`,
        title: '',
        description: '',
        dateOccurred: new Date().toISOString().split('T')[0],
        severity: 'medium',
        status: 'open',
        reportedBy: 'Current User',
        ...formData as Incident
      };
      storageService.addIncident(newIncident);
    }
    setIncidents(storageService.getIncidents());
    setIsModalOpen(false);
    setEditingIncident(null);
    setFormData({});
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure?')) {
      storageService.deleteIncident(id);
      setIncidents(storageService.getIncidents());
    }
  };

  const openModal = (incident?: Incident) => {
    if (incident) {
      setEditingIncident(incident);
      setFormData(incident);
    } else {
      setEditingIncident(null);
      setFormData({});
    }
    setIsModalOpen(true);
  };

  const handleAnalyze = async () => {
    if (!formData.description) return;
    setAnalyzing(true);
    setError(null);
    try {
      const analysis = await aiService.analyzeIncident(formData.description);
      setFormData(prev => ({
        ...prev,
        rootCause: (prev.rootCause ? prev.rootCause + '\n\n' : '') + 'AI Analysis:\n' + analysis
      }));
    } catch (error: any) {
      console.error(error);
      setError(error.message || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const getSeverityColor = (severity: Incident['severity']) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
        <div className="w-full sm:w-auto flex-1">
          <FilterBar 
            onSearch={setSearchQuery}
            filters={[
              {
                label: 'Status',
                value: statusFilter,
                onChange: setStatusFilter,
                options: [
                  { label: 'Open', value: 'open' },
                  { label: 'Investigating', value: 'investigating' },
                  { label: 'Resolved', value: 'resolved' },
                  { label: 'Closed', value: 'closed' },
                ]
              },
              {
                label: 'Severity',
                value: severityFilter,
                onChange: setSeverityFilter,
                options: [
                  { label: 'Low', value: 'low' },
                  { label: 'Medium', value: 'medium' },
                  { label: 'High', value: 'high' },
                  { label: 'Critical', value: 'critical' },
                ]
              },
              {
                label: 'Reported By',
                value: reportedByFilter,
                onChange: setReportedByFilter,
                options: reportedByOptions
              }
            ]}
          />
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportToCSV(incidents, 'incident-register')} className="flex items-center gap-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded hover:bg-gray-50 dark:hover:bg-gray-600 whitespace-nowrap">
            <Download className="w-4 h-4" /> Export
          </button>
          {canEdit && (
            <button onClick={() => openModal()} className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 whitespace-nowrap">
              <Plus className="w-4 h-4" /> Report Incident
            </button>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden mb-6">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Severity</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {paginatedIncidents.map((incident) => (
              <tr 
                key={incident.id} 
                onClick={() => canEdit && openModal(incident)}
                className={canEdit ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700" : ""}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <AlertOctagon className="w-4 h-4 text-gray-400" />
                  {incident.title}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{incident.dateOccurred}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(incident.severity)}`}>
                    {incident.severity}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 capitalize">{incident.status}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {canEdit && (
                    <button onClick={(e) => { e.stopPropagation(); openModal(incident); }} className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 mr-4"><Edit className="w-4 h-4" /></button>
                  )}
                  {canDelete && (
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(incident.id); }} className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        totalItems={filteredIncidents.length}
      />

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingIncident ? 'Edit Incident' : 'Report Incident'}>
        {/* ... Modal Content (same as before) ... */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Title</label>
            <input type="text" className="mt-1 block w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" value={formData.title || ''} onChange={e => setFormData({...formData, title: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date Occurred</label>
              <input type="date" className="mt-1 block w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" value={formData.dateOccurred || ''} onChange={e => setFormData({...formData, dateOccurred: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Incident Type</label>
              <input 
                type="text" 
                list="incident-types" 
                className="mt-1 block w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" 
                value={formData.type || ''} 
                onChange={e => setFormData({...formData, type: e.target.value})} 
                placeholder="Select or type..."
              />
              <datalist id="incident-types">
                {incidentTypes.map(t => <option key={t} value={t} />)}
              </datalist>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Severity</label>
            <select className="mt-1 block w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" value={formData.severity || 'medium'} onChange={e => setFormData({...formData, severity: e.target.value as any})}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea className="mt-1 block w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" rows={3} value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} />
          </div>
          
          <IncidentAdvisorCard 
            description={formData.description || ''} 
            onAnalysisComplete={(result) => {
              setFormData(prev => ({
                ...prev,
                type: result.type,
                severity: result.severity,
                rootCause: result.rootCause,
                correctiveActions: result.correctiveActions,
                affectedSystems: result.affectedSystems
              }));
            }} 
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Root Cause Analysis</label>
            <textarea className="mt-1 block w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" rows={2} value={formData.rootCause || ''} onChange={e => setFormData({...formData, rootCause: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Affected Systems</label>
            <input type="text" className="mt-1 block w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" value={formData.affectedSystems || ''} onChange={e => setFormData({...formData, affectedSystems: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Corrective Actions</label>
            <textarea className="mt-1 block w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" rows={2} value={formData.correctiveActions || ''} onChange={e => setFormData({...formData, correctiveActions: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
            <select className="mt-1 block w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" value={formData.status || 'open'} onChange={e => setFormData({...formData, status: e.target.value as any})}>
              <option value="open">Open</option>
              <option value="investigating">Investigating</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-600 dark:text-gray-400">Cancel</button>
            <button onClick={handleSave} className="px-4 py-2 bg-indigo-600 text-white rounded">Save</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
